import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { 
  Building2, 
  Users, 
  Heart, 
  Mail,
  ArrowRight,
  Star,
  Handshake,
  Code2,
  ExternalLink,
  CheckCircle2,
  Wrench,
  Search
} from 'lucide-react';
import {
  PARTNER_ADOPTION_PHASES,
  PARTNER_ENGAGEMENT_TYPES,
  ADOPTION_PHASE_COLORS,
  ENGAGEMENT_TYPE_COLORS,
} from '@/lib/admin/constants';

interface Partner {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  type: string;
  partner_category: string | null;
  logo_url: string | null;
  website_url: string | null;
  contribution: string | null;
  display_order: number | null;
  is_active: boolean | null;
  is_featured: boolean | null;
  adoption_phase: string | null;
  engagement_type: string | null;
}

interface CommunityStats {
  contributors_count: number | null;
  contributors_label: string;
  schema_reviews_count: number | null;
  schema_reviews_label: string;
  validations_count: number | null;
  validations_label: string;
  use_real_data: boolean;
}

export default function Partners() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    contributors: { count: 0, label: 'Contributors' },
    reviews: { count: 0, label: 'Schema Reviews' },
    validations: { count: 0, label: 'Validations Run' },
  });

  useEffect(() => {
    const fetchData = async () => {
      // Fetch partners
      const { data: partnersData, error: partnersError } = await supabase
        .from('partners')
        .select('*')
        .eq('is_active', true)
        .order('type')
        .order('display_order');

      if (!partnersError && partnersData) {
        setPartners(partnersData);
      }

      // Fetch community stats settings
      const { data: settingsData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'community_stats')
        .maybeSingle();

      const settings = settingsData?.value as unknown as CommunityStats | null;

      if (settings?.use_real_data) {
        // Fetch real counts in parallel
        const [partnerCount, proposalCount, validationCount] = await Promise.all([
          supabase.from('partners').select('*', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('standard_proposals').select('*', { count: 'exact', head: true }),
          supabase.from('analytics_events').select('*', { count: 'exact', head: true }).eq('event_type', 'validation'),
        ]);

        setStats({
          contributors: {
            count: settings?.contributors_count ?? partnerCount.count ?? 0,
            label: settings?.contributors_label ?? 'Contributors',
          },
          reviews: {
            count: settings?.schema_reviews_count ?? proposalCount.count ?? 0,
            label: settings?.schema_reviews_label ?? 'Schema Reviews',
          },
          validations: {
            count: settings?.validations_count ?? validationCount.count ?? 0,
            label: settings?.validations_label ?? 'Validations Run',
          },
        });
      } else if (settings) {
        setStats({
          contributors: {
            count: settings.contributors_count ?? 0,
            label: settings.contributors_label ?? 'Contributors',
          },
          reviews: {
            count: settings.schema_reviews_count ?? 0,
            label: settings.schema_reviews_label ?? 'Schema Reviews',
          },
          validations: {
            count: settings.validations_count ?? 0,
            label: settings.validations_label ?? 'Validations Run',
          },
        });
      }

      setLoading(false);
    };

    fetchData();
  }, []);

  const foundingPartners = partners.filter(p => p.type === 'founding');
  const contributingOrgs = partners.filter(p => p.type === 'contributing');

  const getAdoptionPhaseLabel = (phase: string | null) => {
    return PARTNER_ADOPTION_PHASES.find(p => p.value === phase)?.label || phase;
  };

  const getEngagementTypeLabel = (type: string | null) => {
    return PARTNER_ENGAGEMENT_TYPES.find(t => t.value === type)?.label || type;
  };

  const formatStatCount = (count: number) => {
    if (count === 0) return '—';
    return `${count}+`;
  };

  return (
    <Layout>
      <div className="container py-12 max-w-6xl">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4">Our Partners</Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Built by the Industry, for the Industry
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            AutomotiveAPI is a collaborative effort. These organizations are 
            shaping the future of automotive data standards.
          </p>
        </div>

        {/* Partner Engagement Legend */}
        <section className="mb-12">
          <Card className="bg-muted/30 border-muted">
            <CardContent className="py-6">
              <h3 className="font-semibold mb-4 text-center">How Partners Engage</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    Adoption Phases
                  </h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`${ADOPTION_PHASE_COLORS.exploring}`}>
                        <Search className="h-3 w-3 mr-1" />
                        Exploring
                      </Badge>
                      <span>Evaluating the standard for their systems</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`${ADOPTION_PHASE_COLORS.implementing}`}>
                        <Wrench className="h-3 w-3 mr-1" />
                        Implementing
                      </Badge>
                      <span>Actively developing with the standard</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`${ADOPTION_PHASE_COLORS.adopted}`}>
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Adopted
                      </Badge>
                      <span>Fully integrated in production</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Handshake className="h-4 w-4" />
                    Engagement Types
                  </h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`${ENGAGEMENT_TYPE_COLORS.supporter}`}>
                        <Heart className="h-3 w-3 mr-1" />
                        Supporter
                      </Badge>
                      <span>Endorses the mission and initiative</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`${ENGAGEMENT_TYPE_COLORS.contributor}`}>
                        <Code2 className="h-3 w-3 mr-1" />
                        Contributor
                      </Badge>
                      <span>Helps build and refine standards</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`${ENGAGEMENT_TYPE_COLORS.implementer}`}>
                        <Building2 className="h-3 w-3 mr-1" />
                        Implementer
                      </Badge>
                      <span>Building products using the standard</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Founding Partners */}
        <section className="mb-16">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Star className="h-6 w-6 text-yellow-500" />
            <h2 className="text-2xl font-bold">Founding Partners</h2>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="text-center">
                  <CardContent className="pt-8 pb-6">
                    <Skeleton className="w-24 h-24 rounded-full mx-auto mb-4" />
                    <Skeleton className="h-6 w-32 mx-auto mb-3" />
                    <Skeleton className="h-4 w-48 mx-auto mb-2" />
                    <Skeleton className="h-16 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : foundingPartners.length > 0 ? (
            <div className="grid md:grid-cols-3 gap-6">
              {foundingPartners.map((partner) => (
                <Card key={partner.id} className="text-center group hover:shadow-md transition-shadow">
                  <CardContent className="pt-8 pb-6">
                    {partner.logo_url ? (
                      <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-4 overflow-hidden p-2">
                        <img 
                          src={partner.logo_url} 
                          alt={partner.name} 
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                        <Building2 className="h-10 w-10 text-muted-foreground" />
                      </div>
                    )}
                    <Badge variant="outline" className="mb-3">Founding Partner</Badge>
                    <h3 className="font-semibold text-lg mb-2">{partner.name}</h3>
                    
                    {/* Engagement Badges */}
                    <div className="flex flex-wrap justify-center gap-1 mb-3">
                      {partner.adoption_phase && (
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${ADOPTION_PHASE_COLORS[partner.adoption_phase] || ''}`}
                        >
                          {getAdoptionPhaseLabel(partner.adoption_phase)}
                        </Badge>
                      )}
                      {partner.engagement_type && (
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${ENGAGEMENT_TYPE_COLORS[partner.engagement_type] || ''}`}
                        >
                          {getEngagementTypeLabel(partner.engagement_type)}
                        </Badge>
                      )}
                    </div>

                    {partner.description && (
                      <p className="text-sm text-muted-foreground mb-4">{partner.description}</p>
                    )}
                    {partner.website_url && (
                      <a 
                        href={partner.website_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                      >
                        Visit Website <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Founding partners coming soon</p>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Contributing Organizations */}
        <section className="mb-16">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Handshake className="h-6 w-6 text-primary" />
            <h2 className="text-2xl font-bold">Contributing Organizations</h2>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start gap-3">
                      <Skeleton className="w-10 h-10 rounded-lg" />
                      <div className="flex-1">
                        <Skeleton className="h-5 w-32 mb-2" />
                        <Skeleton className="h-4 w-20 mb-1" />
                        <Skeleton className="h-4 w-full" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : contributingOrgs.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contributingOrgs.map((org) => (
                <Card key={org.id} className="group hover:shadow-md transition-shadow">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start gap-3">
                      {org.logo_url ? (
                        <div className="w-10 h-10 rounded-lg bg-muted shrink-0 overflow-hidden p-1">
                          <img 
                            src={org.logo_url} 
                            alt={org.name} 
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="p-2 rounded-lg bg-muted shrink-0">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-medium">{org.name}</h3>
                          {org.website_url && (
                            <a 
                              href={org.website_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:text-primary/80 shrink-0"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                        
                        {/* Engagement Badges */}
                        <div className="flex flex-wrap gap-1 my-2">
                          {org.adoption_phase && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${ADOPTION_PHASE_COLORS[org.adoption_phase] || ''}`}
                            >
                              {getAdoptionPhaseLabel(org.adoption_phase)}
                            </Badge>
                          )}
                          {org.engagement_type && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${ENGAGEMENT_TYPE_COLORS[org.engagement_type] || ''}`}
                            >
                              {getEngagementTypeLabel(org.engagement_type)}
                            </Badge>
                          )}
                        </div>

                        {org.partner_category && (
                          <p className="text-xs text-muted-foreground mb-1">{org.partner_category}</p>
                        )}
                        {org.contribution && (
                          <p className="text-sm text-muted-foreground">{org.contribution}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <Handshake className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Contributing organizations coming soon</p>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Community Contributors */}
        <section className="mb-16">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Code2 className="h-6 w-6 text-green-500" />
            <h2 className="text-2xl font-bold">Community Contributors</h2>
          </div>
          
          <Card>
            <CardContent className="py-8 text-center">
              <div className="p-4 rounded-full bg-green-500/10 w-fit mx-auto mb-4">
                <Users className="h-10 w-10 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Open Source Community</h3>
              <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
                AutomotiveAPI standards are developed in the open. Individual contributors 
                from across the automotive industry help refine schemas, report issues, 
                and improve documentation.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="text-center px-6">
                  <p className="text-3xl font-bold text-primary">
                    {formatStatCount(stats.contributors.count)}
                  </p>
                  <p className="text-sm text-muted-foreground">{stats.contributors.label}</p>
                </div>
                <div className="text-center px-6">
                  <p className="text-3xl font-bold text-primary">
                    {formatStatCount(stats.reviews.count)}
                  </p>
                  <p className="text-sm text-muted-foreground">{stats.reviews.label}</p>
                </div>
                <div className="text-center px-6">
                  <p className="text-3xl font-bold text-primary">
                    {formatStatCount(stats.validations.count)}
                  </p>
                  <p className="text-sm text-muted-foreground">{stats.validations.label}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Become a Partner */}
        <section className="mb-16">
          <Card className="bg-primary/5 border-primary/20">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <Heart className="h-10 w-10 text-primary" />
              </div>
              <CardTitle className="text-2xl">Become a Partner</CardTitle>
            </CardHeader>
            <CardContent className="text-center pb-8">
              <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                Join the growing community of organizations shaping automotive data standards. 
                Whether you're a vendor, dealer group, or industry association, there's a place for you.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="p-4">
                  <h4 className="font-semibold mb-2">For Vendors</h4>
                  <p className="text-sm text-muted-foreground">
                    Implement standards in your products. Reduce integration costs. 
                    Differentiate with compatibility.
                  </p>
                </div>
                <div className="p-4">
                  <h4 className="font-semibold mb-2">For Dealers</h4>
                  <p className="text-sm text-muted-foreground">
                    Demand standards from your vendors. Validate data quality. 
                    Simplify multi-vendor operations.
                  </p>
                </div>
                <div className="p-4">
                  <h4 className="font-semibold mb-2">For Developers</h4>
                  <p className="text-sm text-muted-foreground">
                    Contribute to schemas. Report issues. Build tools. 
                    Join the open source community.
                  </p>
                </div>
              </div>
              
              <Button asChild size="lg">
                <Link to="/contact?type=partner">
                  <Mail className="mr-2 h-4 w-4" />
                  Contact Us to Partner
                </Link>
              </Button>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center">
          <Card className="bg-muted/50">
            <CardContent className="py-12">
              <h2 className="text-2xl font-bold mb-4">See the Standards in Action</h2>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Explore the data standards our partners helped build and start 
                using them in your own integrations.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild size="lg">
                  <Link to="/api-standards">
                    Browse Standards
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg">
                  <Link to="/about">
                    Learn Our Mission
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
}
